import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { InputNumber } from '@components/framework/input/InputNumber';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { LovWithoutIdDTO } from '@api/ringil-api.v2';
import { RC, RR } from '@components/obsolete/RC.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import { CargoTypeCodeEnum } from '@utils/constants.ts';
import { ValidationError } from 'yup';
import { findValidationMessage } from '@utils/validation.tsx';
import { deferStampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  value?: CargoType;
  onChange: (cargoType: CargoType) => void;
  inSidebar: boolean;
  disabled?: boolean;
  validationErrors?: ValidationError[];
  dataTestId?: string;
}

export interface CargoType {
  cargoTypeCode: string;
  adrWeight?: number;
}

export const CargoTypeComponent: React.FC<ComponentProps> = ({ value, inSidebar, onChange, disabled = false, validationErrors, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const { lovSHSNCargoTypes } = useContext(ApiContext);
  const [cargoTypes, setCargoTypes] = useState<LovWithoutIdDTO[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    lovSHSNCargoTypes(d => {
      setCargoTypes(d);
      if (value && value.cargoTypeCode !== CargoTypeCodeEnum.ADR_LQ) {
        onChange({ ...value, adrWeight: null });
      }
    });
  }, []);

  useEffect(() => {
    return deferStampDataTestIdOnDescendantRef(wrapperRef, '.p-dropdown', dataTestId);
  }, [cargoTypes, dataTestId]);

  return (
    <>
      <RR>
        <RC large={inSidebar ? 12 : 3} medium={inSidebar ? 12 : 6} small={12}>
          <HorizontalFieldValue
            required={true}
            label={tr(`CargoType.cargoType`, `Cargo type`)}
            value={
              <div ref={wrapperRef}>
                <Dropdown
                  disabled={disabled}
                  options={cargoTypes}
                  optionValue={'code'}
                  optionLabel={'name'}
                  value={value?.cargoTypeCode}
                  style={{ width: '100%' }}
                  onChange={e => onChange({ ...(value || {}), cargoTypeCode: e.value } as CargoType)}
                  filter
                  itemTemplate={(option: LovWithoutIdDTO) => (
                    <span data-testid={dataTestId ? `${dataTestId}-${toTestIdSlug(option?.name || option?.code || '')}` : undefined}>{option?.name}</span>
                  )}
                />
              </div>
            }
          />
        </RC>
        {value?.cargoTypeCode === CargoTypeCodeEnum.ADR_LQ && (
          <RC large={inSidebar ? 12 : 3} medium={inSidebar ? 12 : 6} small={12}>
            <HorizontalFieldValue
              required={true}
              label={tr('CargoType.adrWeightKg', 'ADR weight [kg]')}
              validationError={findValidationMessage(validationErrors, 'cargoType.adrWeight', tr)}
              value={
                <InputNumber
                  value={value?.adrWeight}
                  onChange={e => onChange({ ...(value || {}), adrWeight: e.value } as CargoType)}
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  locale={'cs'}
                  style={{ width: '100%' }}
                />
              }
            />
          </RC>
        )}
      </RR>
    </>
  );
};
